import ApiClient from './ApiClient';
import SubscriptionRequest from '../models/entities/SubscriptionRequest';
import { AzureResourceModel, ImportSubscriptionModel, RequestSubscriptionModel, UpdateSubscriptionModel, GrantConsentIdentity } from '../models/viewModels/Subscriptions';
import RejectionModel from '../models/interfaces/RejectionModel';
import PolicyExemptionRequest from '../models/entities/PolicyExemptionRequest';
import { PolicyAssignmentResponse } from '../models/viewModels/PolicyExemptionRequests/PolicyAssignmentResponse';
import ChangeHistory from '../models/entities/ChangeHistory';
import EntityChangeHistoryService from '../models/interfaces/EntityChangeHistoryService';
import { VirtualNetwork } from '../models/entities/AzureResources/VirtualNetwork';
import { GrantResponse } from '../models/entities/GrantResponse';
import { VirtualMachineModel } from '../models/viewModels/Subscriptions/VMProvisioningModel';
import CompletedPORRules from '../models/entities/CompletedPORRules';

export interface Publishers {
	[key: string]: AzureResourceModel[];
  }

export default class SubscriptionRequestsService extends ApiClient implements EntityChangeHistoryService {
	async getAll() {
		return this.get<SubscriptionRequest[]>('SubscriptionRequests');
	}

	async getMySubscriptions() {
		return this.get<SubscriptionRequest[]>('SubscriptionRequests/me');
	}

	async getOwnedSubscriptions() {
		return this.get<SubscriptionRequest[]>('SubscriptionRequests/owned');
	}

	async getById(id: number) {
		return this.get<SubscriptionRequest>(`SubscriptionRequests/${id}`);
	}

	async getPermissionsInSubscription(id: number, resourceGroupName: string) {
		return this.get<any[]>(`SubscriptionRequests/${id}/permissions/${resourceGroupName}`);
	}

	async getByIdValidateAccessPOR(id: number) {
		return this.get<SubscriptionRequest>(`SubscriptionRequests/${id}?includePORs=true`);
	}

	async getPORBySubscription(subscriptionId: number, porId: number) {
		if (isNaN(porId)) {
			return this.get<SubscriptionRequest>(`SubscriptionRequests/${subscriptionId}/por/`);
		}
		return this.get<SubscriptionRequest>(`SubscriptionRequests/${subscriptionId}/por/${porId}`);
	}

	// Get completed POR in the sub
	async getCompletedPORRules(subscriptionId: number) {
		return this.get<CompletedPORRules>(`SubscriptionRequests/${subscriptionId}/CompletedPORRules`);
	}

	async create(model: RequestSubscriptionModel) {
		return this.post<SubscriptionRequest>('SubscriptionRequests', model);
	}

	async createConnected(model: RequestSubscriptionModel) {
		return this.post<SubscriptionRequest>('SubscriptionRequests/requestConnected', model);
	}

	async import(model: ImportSubscriptionModel) {
		return this.post<SubscriptionRequest>('SubscriptionRequests/import', model);
	}

	async update(id: number, model: UpdateSubscriptionModel) {
		return this.patch<SubscriptionRequest>(`SubscriptionRequests/${id}`, model);
	}

	async updateMultiple(model: UpdateSubscriptionModel[]) {
		return this.patch<SubscriptionRequest>(`SubscriptionRequests/`, model);
	}

	async approve(id: number) {
		return this.patch<SubscriptionRequest>(`SubscriptionRequests/${id}/approval`, null);
	}

	async reject(id: number, model: RejectionModel) {
		return this.patch<null>(`SubscriptionRequests/${id}/rejection`, model);
	}
	
	async getAllResourceGroupsInSubscription(subscriptionId: number, isRulesAdmin = false) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/ResourceGroups/${isRulesAdmin}`);
	}

	async getAllLocationsInSubscription(subscriptionId: number) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/Locations`);
	}

	async getAllSizeInSubscription(subscriptionId: number, location: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/Size/${location}`);
	}

	async getAllRecoveryServicesByResourceGroupInSubscription(subscriptionId: number) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/RecoveryServices`);
	}

	async getAllBackupPoliciesInSubscription(subscriptionId: number, resourceGroup: string, vault: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/BackupPolicies/${resourceGroup}/${vault}`);
	}

	async getAllLinuxPublishers() {
		return this.get<string[]>(`SubscriptionRequests/LinuxPublishers`);
	}

	async getAllPublishersByLocationInSubscription(subscriptionId: number, location: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/Publishers/${location}`);
	}

	async getAllOffersByPublisherInSubscription(subscriptionId: number, location: string, publisher: string, os: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/Offers/${location}/${publisher}/${os}`);
	}

	async getAllSKUsByOfferInSubscription(subscriptionId: number, location: string, publisher: string, offer: string, os:string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/SKUs/${location}/${publisher}/${offer}/${os}`);
	}

	async getAllDiskSizesByLocationInSubscription(subscriptionId: number, location: string, diskType: string) {
		return this.get<string[]>(`SubscriptionRequests/${subscriptionId}/DiskSizes/${location}/${diskType}`);
	}

	async getAllResourcesInResourceGroup(subscriptionId: number,  resourceGroupId: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/Resources?resourceGroupId=${resourceGroupId}`);
	}

	async getAllResourcesWithIdentities(subscriptionId: number,  resourceGroupId: string) {
		return this.get<AzureResourceModel[]>(`SubscriptionRequests/${subscriptionId}/ResourcesWithIdentities?resourceGroupId=${resourceGroupId}`);
	}

	async getPolicyExemptionRequestsBySubsId(id: number) {
		return this.get<PolicyExemptionRequest[]>(`SubscriptionRequests/${id}/PolicyExemptions`);
	}

	async getSubscriptionPolicyAssignmentsById(id: number) {
		return this.get<PolicyAssignmentResponse[]>(`SubscriptionRequests/${id}/policiesDefinitions`);
	}

	async getChangeHistory(id: number) {
		return this.get<ChangeHistory[]>(`SubscriptionRequests/${id}/history`);
	}

	async getAddressSpaces(id: number) {
		return this.get<VirtualNetwork[]>(`SubscriptionRequests/${id}/virtualNetworks`);
	}

	async getSubnetsFromVirtualNetwork(id: number, resourceGroup: string, vNet: string) {
		return this.get<VirtualNetwork[]>(`SubscriptionRequests/${id}/subnets/${resourceGroup}/${vNet}`);
	}

	async getAddressSpacesByResourceGroup(id: number, resourceGroupName: string) {
		return this.get<VirtualNetwork[]>(`SubscriptionRequests/${id}/vNets?resourceGroupName=${resourceGroupName}`);
	}

	async grantAdminConsent(subscriptionId: number, principalId: string, model: GrantConsentIdentity){
		return this.post<GrantResponse>(`SubscriptionRequests/${subscriptionId}/permissionsAndConsent/${principalId}`, model);
	}

	async getOrgAdminSubscriptions(){
		return this.get<SubscriptionRequest>('SubscriptionRequests/OrgAdminSubscriptions/me');
	}

	async createVM(subscriptionId: number, model: VirtualMachineModel){
		return this.post<any>(`SubscriptionRequests/${subscriptionId}/createVM`, model);
	}

	async subscriptionsLookup(nameOrGuid: string){
		return this.get<SubscriptionRequest[]>(`SubscriptionRequests/SubsLookup?nameOrGuid=${nameOrGuid}&maxRows=10`);
	}

	async attest(id: number) {
		return this.patch<SubscriptionRequest>(`SubscriptionRequests/${id}/attest`, null);
	}

	async getSubscriptionsRequiringAttestation() {
		return this.get<SubscriptionRequest[]>('SubscriptionRequests/requiringAttestation');
	}
}
